import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, ArrowRight, Check, Gift } from "lucide-react";
import { submitLead } from "@/lib/leads.functions";
import { trackEvent } from "@/lib/analytics";
import imgEvent from "@/assets/parfum-bar-mariage.jpg.asset.json";
import imgFormule from "@/assets/parfum-flacons.jpg.asset.json";
import imgContact from "@/assets/parfum-flacon-maries.jpg.asset.json";

const EVENTS = ["Mariage", "Anniversaire", "Entreprise / séminaire", "Autre événement"];
const FORMULES = [
  { name: "Essentielle", price: 12, desc: "Flacon 15 ml personnalisé" },
  { name: "Signature", price: 14, desc: "Flacon 30 ml + étiquette · la plus choisie" },
  { name: "Prestige", price: 17, desc: "Flacon 50 ml + coffret cadeau" },
];
const IMAGES = [imgEvent.url, imgFormule.url, imgContact.url];

export function BarParfumFunnel() {
  const submit = useServerFn(submitLead);
  const [step, setStep] = useState(0);
  const [event, setEvent] = useState("");
  const [guests, setGuests] = useState(80);
  const [formule, setFormule] = useState("Signature");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const price = FORMULES.find((f) => f.name === formule)!.price;
  const total = Math.max(50, guests) * price;

  const go = (n: number) => {
    setStep(n);
    trackEvent("bar_parfum_funnel_step", { step: n + 1 });
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const nom = String(f.get("nom") || "").trim();
    const telephone = String(f.get("telephone") || "").trim();
    if (!nom || telephone.replace(/\D/g, "").length < 9) {
      setErr("Merci d'indiquer votre nom et un numéro de téléphone valide.");
      return;
    }
    setErr(null);
    setSending(true);
    try {
      await submit({
        data: {
          nom: nom.slice(0, 120),
          telephone: telephone.slice(0, 40),
          type_event: `Bar à parfum — ${event || "Événement"}`,
          date_event: String(f.get("date") || "") || null,
          message: `Formule ${formule} (${price} €/invité) · ${guests} invités · estimation ${total} €. Lieu : ${String(f.get("lieu") || "?").slice(0, 200)}`,
          source: "bar-parfum-funnel",
          hp: String(f.get("website") || ""),
        },
      });
      trackEvent("generate_lead", { form: "bar_parfum_funnel" });
      setDone(true);
    } catch {
      setErr("Envoi impossible pour le moment. Appelez-moi au 07 67 90 86 67.");
    } finally {
      setSending(false);
    }
  };

  const input = "h-11 w-full rounded border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";
  const choice = (active: boolean) =>
    `rounded border px-4 py-3 text-left text-sm transition-colors ${active ? "border-[var(--gold)] bg-[var(--gold)]/10 text-foreground font-semibold" : "border-foreground/15 text-foreground hover:border-foreground/40"}`;

  return (
    <section id="reserver" className="scroll-mt-24 py-20 px-6 bg-card border-y border-foreground/10">
      <div className="max-w-5xl mx-auto grid overflow-hidden rounded-lg border border-foreground/10 bg-background shadow-xl md:grid-cols-[0.9fr_1.1fr]">
        <div className="relative h-48 md:h-auto">
          <img src={IMAGES[Math.min(step, 2)]} alt="Bar à parfum de L'Atelier Olfactif" className="h-full w-full object-cover" loading="lazy" />
        </div>
        <div className="p-6 md:p-10">
          <p className="eyebrow text-foreground">Réservation en 3 étapes · 1 minute</p>
          <div className="mt-4 flex gap-2" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= step || done ? "bg-[var(--gold)]" : "bg-foreground/15"}`} />
            ))}
          </div>

          {done ? (
            <div role="status" className="mt-8">
              <Check className="h-10 w-10 text-[var(--gold)]" />
              <h3 className="mt-4 font-serif-display text-3xl text-foreground">Merci, c'est noté !</h3>
              <p className="mt-3 text-sm leading-relaxed">
                Je vous rappelle sous 24 h pour confirmer la disponibilité de votre date et votre devis ({total} € estimés, formule {formule}).
              </p>
            </div>
          ) : step === 0 ? (
            <div className="mt-6">
              <h3 className="font-serif-display text-3xl text-foreground">Quel événement préparez-vous ?</h3>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {EVENTS.map((ev) => (
                  <button key={ev} type="button" className={choice(event === ev)} onClick={() => { setEvent(ev); go(1); }}>
                    {ev}
                  </button>
                ))}
              </div>
              <p className="mt-6 flex gap-2 text-xs text-foreground"><Gift className="h-4 w-4 shrink-0" /> 5 premiers mariages 2027 : étiquettes aux prénoms des mariés offertes.</p>
            </div>
          ) : step === 1 ? (
            <div className="mt-6">
              <h3 className="font-serif-display text-3xl text-foreground">Votre formule</h3>
              <label className="mt-5 block text-sm font-medium text-foreground">
                Nombre d'invités : <strong>{guests}</strong>
                <input type="range" min={50} max={250} step={5} value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="mt-2 w-full accent-[var(--gold)]" />
              </label>
              <div className="mt-4 grid gap-2">
                {FORMULES.map((f) => (
                  <button key={f.name} type="button" className={choice(formule === f.name)} onClick={() => setFormule(f.name)}>
                    <span className="flex justify-between gap-3"><span>{f.name}</span><span>{f.price} €/invité</span></span>
                    <span className="block text-xs font-normal text-muted-foreground mt-1">{f.desc}</span>
                  </button>
                ))}
              </div>
              <p className="mt-5 font-serif-display text-2xl text-foreground">Estimation : <span className="gold-text-shine">{total} €</span></p>
              <div className="mt-6 flex justify-between gap-3">
                <button type="button" onClick={() => go(0)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground"><ArrowLeft className="h-4 w-4" /> Retour</button>
                <button type="button" onClick={() => go(2)} className="inline-flex items-center gap-2 bg-[var(--gold)] px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground hover:brightness-110">Continuer <ArrowRight className="h-4 w-4" /></button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="mt-6 grid gap-3">
              <h3 className="font-serif-display text-3xl text-foreground">Je bloque votre date</h3>
              <p className="text-xs text-foreground">{event} · {formule} · {guests} invités · <strong>{total} €</strong></p>
              <input name="nom" placeholder="Vos prénoms" aria-label="Vos prénoms" autoComplete="name" className={input} maxLength={120} />
              <input name="telephone" type="tel" placeholder="Téléphone" aria-label="Téléphone" autoComplete="tel" className={input} maxLength={40} />
              <div className="grid grid-cols-2 gap-3">
                <input name="date" type="date" aria-label="Date de l'événement" className={input} />
                <input name="lieu" placeholder="Lieu / ville" aria-label="Lieu" className={input} maxLength={200} />
              </div>
              <input name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              {err && <p role="alert" className="text-xs text-destructive">{err}</p>}
              <div className="mt-2 flex justify-between gap-3">
                <button type="button" onClick={() => go(1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground"><ArrowLeft className="h-4 w-4" /> Retour</button>
                <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-[var(--gold)] px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground hover:brightness-110 disabled:opacity-60">{sending ? "Envoi…" : "Recevoir mon devis"} <ArrowRight className="h-4 w-4" /></button>
              </div>
              <p className="text-[11px] text-muted-foreground">Sans engagement · réponse sous 24 h</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
